import { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EventMemberPicker = ({ selected, onChange, excludeIds = [], disabled = false }) => {
  const [query, setQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    setMembers([]); setError('');
    if (query.trim().length < 2) { setBusy(false); return; }
    let active = true;
    const controller = new AbortController();
    setBusy(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/social-events/invite-members/search`, { params: { q: query.trim() }, signal: controller.signal });
        if (active) setMembers(data.users);
      } catch (err) {
        if (active && !axios.isCancel(err)) setError('Could not load members.');
      } finally { if (active) setBusy(false); }
    }, 300);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [query, retry]);
  const available = members.filter(m => !excludeIds.includes(m.user_id));
  const toggle = (member) => onChange(selected.some(m => m.user_id === member.user_id)
    ? selected.filter(m => m.user_id !== member.user_id) : [...selected, member]);

  return <section className="space-y-3 min-w-0" data-testid="event-member-picker">
    <label htmlFor="event-member-search" className="text-sm font-medium">Invite members <span className="text-[var(--ps-text-muted)]" data-testid="event-selected-count">({selected.length}/100)</span></label>
    <div className="relative"><Search className="absolute left-3 top-3 w-4 h-4 text-[var(--ps-text-muted)]" />
      <Input id="event-member-search" data-testid="event-member-search" maxLength={80} className="dark-input pl-9" placeholder="Search name or username" value={query} onChange={e => setQuery(e.target.value)} disabled={disabled} />
    </div>
    {selected.length > 0 && <div className="flex flex-wrap gap-2" data-testid="event-selected-members">
      {selected.map(member => <span key={member.user_id} className="inline-flex items-center gap-1 max-w-full rounded-md bg-[var(--ps-bg-hover)] px-2 py-1 text-sm">
        <span className="break-words min-w-0">{member.name || member.username || 'Member'}</span>
        <button type="button" className="shrink-0 p-1 hover:text-[var(--ps-primary)]" disabled={disabled} onClick={() => toggle(member)} aria-label={`Remove ${member.name || 'member'}`} data-testid={`event-remove-member-${member.user_id}`}><X size={14} /></button>
      </span>)}
    </div>}
    {busy && <p role="status" className="text-sm flex gap-2" data-testid="event-member-loading"><Loader2 size={16} className="animate-spin" /> Searching…</p>}
    {error && <div role="alert" data-testid="event-member-error" className="text-sm text-red-500">{error} <Button type="button" variant="ghost" onClick={() => setRetry(v => v + 1)} data-testid="event-member-retry">Retry</Button></div>}
    {!busy && !error && query.trim().length >= 2 && !available.length && <p className="text-sm text-[var(--ps-text-muted)]" data-testid="event-member-empty">No matching members.</p>}
    {!busy && !error && available.length > 0 && <div className="max-h-48 overflow-y-auto divide-y divide-[var(--ps-border)]" data-testid="event-member-results">
      {available.map(member => { const checked = selected.some(m => m.user_id === member.user_id); return <label key={member.user_id} className="flex items-center gap-3 py-3 cursor-pointer hover:bg-[var(--ps-bg-hover)]">
        <input type="checkbox" checked={checked} disabled={disabled || (!checked && selected.length >= 100)} onChange={() => toggle(member)} data-testid={`event-select-member-${member.user_id}`} className="accent-[var(--ps-primary)] w-4 h-4 shrink-0" />
        <span className="min-w-0 break-words text-sm">{member.name || 'Member'}{member.username && <span className="block text-xs text-[var(--ps-text-muted)]">@{member.username}</span>}</span>
      </label>; })}
    </div>}
  </section>;
};